// The role-dependent landing footer (brief §5.1 roles; prototype landingView): the OPERATOR gets the
// "Compose a new workflow" affordance routing to the Composer (the operator console — surface-contracts);
// the manager (admin_md) gets the "built for you" panel with the request affordance. No dashboard route
// exists for a workflow request in this build, so that affordance starts a FRESH global-chat request (the
// manager's words still route through the one on-record chat transport). Below both sits the one-inbox note
// (N-APPROVE-1: verdicts also
// surface in Approvals and Today's "Needs you"; each row opens the card).

import type { DashboardRole } from '../../../../app/surfaces.ts';
import { PlusGlyph, ShieldGlyph } from '../landing-icons.tsx';

export interface LandingFooterProps {
  readonly role: DashboardRole;
  readonly onOpenComposer: () => void;
  readonly onRequestWorkflow: () => void;
}

function ComposeAffordance({ onOpen }: { readonly onOpen: () => void }): React.JSX.Element {
  return (
    <button
      type="button"
      onClick={onOpen}
      className="mt-6 flex w-full items-center gap-3 rounded-md border border-dashed border-sage bg-surface px-4 py-3.5 text-left text-sm font-semibold text-forest hover:bg-forest-tint"
    >
      <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-sm bg-forest-tint">
        <PlusGlyph size={14} />
      </span>
      Compose a new workflow — describe it, Ritona drafts the configuration
      <span className="ritona-numeric ml-auto inline-flex shrink-0 items-center gap-1.5 text-xs uppercase tracking-wide text-bracken">
        <ShieldGlyph size={11} />
        operator
      </span>
    </button>
  );
}

function RequestPanel({ onRequest }: { readonly onRequest: () => void }): React.JSX.Element {
  return (
    <div className="mt-6 rounded-md border border-dashed border-border bg-surface-sunken px-6 py-5">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
        <div className="min-w-0">
          <div className="ritona-voice text-base text-text">Workflows are built for you</div>
          <div className="mt-1 text-sm text-text-muted">
            Your Ritona engineer composes and activates each workflow with you. Need a new one, or a
            change to a board?
          </div>
        </div>
        <button
          type="button"
          onClick={onRequest}
          className="shrink-0 rounded-sm border border-border bg-surface px-3 py-1.5 text-sm font-medium text-text hover:border-border-strong sm:ml-auto"
        >
          Request a workflow
        </button>
      </div>
    </div>
  );
}

/** The role-scoped composition affordance plus the one-inbox note. */
export function LandingFooter({
  role,
  onOpenComposer,
  onRequestWorkflow,
}: LandingFooterProps): React.JSX.Element {
  return (
    <div>
      {role === 'operator' ? (
        <ComposeAffordance onOpen={onOpenComposer} />
      ) : (
        <RequestPanel onRequest={onRequestWorkflow} />
      )}
      <div className="mt-6 flex flex-wrap items-baseline gap-x-3 gap-y-1 text-sm text-text-muted">
        <span className="ritona-numeric text-xs uppercase tracking-eyebrow text-forest">
          One inbox
        </span>
        <span>
          Verdicts also surface in Approvals and Today’s “Needs you”. Each row opens the card — you
          always rule with the evidence in view.
        </span>
      </div>
    </div>
  );
}
